// src/pages/projects/ProjectDocuments.jsx - Updated with unified permissions
import React from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import {
  Button,
  Paper,
  List,
  Divider,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { EmptyState } from '../../design-system/components/core';
import DetailPageLayout from '../../components/layout/DetailPageLayout';
import useProjectDocuments from './hooks/useProjectDocuments';
import { getFileIcon } from '../../constants/documentConstants';
import useProjectPermissions from '../../hooks/useProjectPermissions';

// Import our components
import DocumentListItem from './components/DocumentListItem';
import UploadDocumentDialog from './components/UploadDocumentDialog';
import EditDocumentDialog from './components/EditDocumentDialog';
import DocumentFilterBar from './components/DocumentFilterBar';

const ProjectDocuments = () => {
  const { id } = useParams();
  // Get project data from outlet context (still need project data)
  const { project, canEdit } = useOutletContext();
  
  // Use the unified permissions hook
  const {
    canViewFiles,
    canUploadFiles,
    canDeleteFiles
  } = useProjectPermissions(id);
  
  // Determine permissions (with backward compatibility)
  // If id is available, use specific permissions; otherwise fall back to canEdit
  const allowUpload = id ? canUploadFiles : canEdit;
  const allowDelete = id ? canDeleteFiles : canEdit;
  const allowView = id ? canViewFiles : true; // Default to true if no projectId
  
  const {
    isUploadDialogOpen,
    setIsUploadDialogOpen,
    isEditDialogOpen,
    editingDocument,
    categoryFilter,
    setCategoryFilter,
    searchTerm,
    setSearchTerm,
    uploadData,
    setUploadData,
    isLoading,
    error,
    handleFileSelect,
    handleUploadDocument,
    handleDeleteDocument,
    handleEditDocument,
    handleSaveDocumentChanges,
    handleCloseEditDialog,
    getFilteredDocuments,
    ConfirmDialogComponent
  } = useProjectDocuments(id);
  
  // If user can't view files and we have projectId, show access denied
  if (id && !allowView && !isLoading) {
    return (
      <EmptyState
        title="Access Denied"
        description="You don't have permission to view documents for this project."
        action={
          <Button variant="contained" component={Link} to={`/projects/${id}`}>
            Back to Project
          </Button>
        }
      />
    );
  }
  
  // Get filtered documents
  const filteredDocuments = project ? getFilteredDocuments(project.documents || []) : [];
  
  // Custom filter component
  const filterComponent = (
    <DocumentFilterBar
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      categoryFilter={categoryFilter}
      setCategoryFilter={setCategoryFilter}
    />
  );
  
  // Action buttons - only show if user can upload files
  const actionButtons = allowUpload ? [
    {
      label: 'Upload Document',
      icon: <AddIcon />,
      onClick: () => setIsUploadDialogOpen(true),
      variant: 'contained'
    }
  ] : [];
  
  // Content area
  const documentsList = (
    filteredDocuments.length > 0 ? (
      <Paper>
        <List>
          {filteredDocuments.map((document, index) => (
            <React.Fragment key={document._id || index}>
              {index > 0 && <Divider />}
              <DocumentListItem
                document={document}
                fileType={getFileIcon(document.mimeType)}
                canEdit={allowUpload} // Use upload permission for editing
                projectId={id}
                onDelete={handleDeleteDocument}
                onEdit={handleEditDocument}
              />
            </React.Fragment>
          ))}
        </List>
      </Paper>
    ) : null
  );
  
  // Empty state component
  const emptyComponent = (
    <EmptyState
      title={searchTerm || categoryFilter !== 'all' 
        ? "No documents match your search or filter criteria"
        : "No documents have been uploaded yet"}
      description={searchTerm || categoryFilter !== 'all'
        ? "Try adjusting your search terms or filters"
        : allowUpload 
          ? "Upload your first document to get started"
          : "Documents will appear here once they are uploaded"}
      action={
        allowUpload && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsUploadDialogOpen(true)}
          >
            Upload Document
          </Button>
        )
      }
    />
  );
  
  return (
    <>
      <DetailPageLayout
        title="Project Documents"
        breadcrumbs={[
          { label: 'Projects', to: '/projects' },
          { label: project?.name, to: `/projects/${id}` },
          { label: 'Documents' }
        ]}
        actionButtons={actionButtons}
        filterComponent={filterComponent}
        isLoading={isLoading}
        error={error}
        isEmpty={filteredDocuments.length === 0 || !project}
        emptyStateComponent={
          !project ? (
            <EmptyState
              title="Project not found"
              description="The project you're looking for doesn't exist or you don't have access to it."
              action={
                <Button variant="contained" component={Link} to="/projects">
                  Back to Projects
                </Button>
              }
            />
          ) : emptyComponent
        }
      >
        {documentsList}
      </DetailPageLayout>
      
      {/* Upload Dialog - only render if user has upload permission */}
      {allowUpload && (
        <UploadDocumentDialog
          open={isUploadDialogOpen}
          onClose={() => setIsUploadDialogOpen(false)}
          uploadData={uploadData}
          setUploadData={setUploadData}
          onFileSelect={handleFileSelect}
          onUpload={handleUploadDocument}
          projectId={id}
        />
      )}

      {/* Edit Document Dialog - only render if user has upload permission */}
      {allowUpload && (
        <EditDocumentDialog
          open={isEditDialogOpen}
          onClose={handleCloseEditDialog}
          document={editingDocument}
          onSave={handleSaveDocumentChanges}
          loading={isLoading}
        />
      )}
      
      {/* Confirm Dialog */}
      <ConfirmDialogComponent />
    </>
  );
};

export default ProjectDocuments;